import { MDBTypography, MDBInput, MDBInputGroup } from 'mdb-react-ui-kit';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';
import { Editor } from 'react-draft-wysiwyg';
import SlugField from '../../../components/toolbox/SlugField';
import { pickSourceText } from '../../../utils/slugify';

const EDITOR_TOOLBAR = {
  options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'colorPicker', 'link', 'embedded', 'emoji', 'image', 'remove', 'history'],
};

export default function ProductContentSection({
  productData,
  setProductName,
  editorState,
  onEditorStateChange,
  getFieldError,
  markFieldTouched,
  canEditSlug,
  setProductSlug,
}) {
  const { t, i18n } = useTranslation();

  const nameDe = productData?.name?.find(n => n.language === "de")?.text || "";
  const nameEn = productData?.name?.find(n => n.language === "en")?.text || "";

  const nameDeError = getFieldError('name_de');
  const nameEnError = getFieldError('name_en');
  const contentDeError = getFieldError('content_de');
  const contentEnError = getFieldError('content_en');

  return (
    <>
      {/* Title */}
      <div className="mb-2 mb-lg-4">
        <MDBTypography tag="h6" className="text-muted">
          {t("Enter the title of the product (max. 60 characters)")}
        </MDBTypography>
      </div>

      {/* German title */}
      <MDBInputGroup
        className="mb-1"
        textAfter={
          <span className={nameDe.length === 60 ? "text-danger fw-bold" : ""}>
            {nameDe.length}/60
          </span>
        }
      >
        <div className="input-group-text">
          <ReactCountryFlag countryCode="de" svg style={{ width: "20px", height: "20px" }} title={i18n.language} />
        </div>
        <MDBInput
          label={t("Title German")}
          type="text"
          maxLength="60"
          className={nameDeError ? "is-invalid" : ""}
          value={nameDe}
          onChange={(e) => setProductName("de", e.target.value)}
          onBlur={() => markFieldTouched('name_de')}
        />
      </MDBInputGroup>
      {nameDeError && <div className="text-danger small mb-2">{nameDeError}</div>}

      {/* English title */}
      <MDBInputGroup
        className="mb-1"
        textAfter={
          <span className={nameEn.length === 60 ? "text-danger fw-bold" : ""}>
            {nameEn.length}/60
          </span>
        }
      >
        <div className="input-group-text">
          <ReactCountryFlag countryCode="gb" svg style={{ width: "20px", height: "20px" }} title={i18n.language} />
        </div>
        <input
          className={`form-control ${nameEnError ? "is-invalid" : ""}`}
          placeholder={t("Title English")}
          type="text"
          maxLength="60"
          value={nameEn}
          onChange={(e) => setProductName("en", e.target.value)}
          onBlur={() => markFieldTouched('name_en')}
        />
      </MDBInputGroup>
      {nameEnError && <div className="text-danger small mb-2">{nameEnError}</div>}

      {/* Public URL slug (Admin/Moderator only) */}
      {canEditSlug && (
        <div className="mt-3">
          <SlugField
            entity="product"
            entityId={productData?.id}
            value={productData?.slug}
            history={productData?.slugHistory}
            sourceText={pickSourceText(productData?.name)}
            onChange={setProductSlug}
          />
        </div>
      )}

      {/* Description */}
      <div className="mb-2 mb-lg-4 mt-4">
        <MDBTypography tag="h6" className="text-muted">
          {t("Product description. (Min. 500 characters)")}
        </MDBTypography>
      </div>

      {/* German editor */}
      <ReactCountryFlag countryCode="de" svg style={{ width: "20px", height: "20px" }} title={i18n.language} />
      <div onBlur={() => markFieldTouched('content_de')}>
        <Editor
          editorState={editorState["de"]}
          editorClassName={`md-textarea form-control ${contentDeError ? "is-invalid" : ""}`}
          onEditorStateChange={e => onEditorStateChange(e, "de")}
          toolbar={EDITOR_TOOLBAR}
        />
      </div>
      {contentDeError && <div className="text-danger small mb-2">{contentDeError}</div>}

      <br />

      {/* English editor */}
      <ReactCountryFlag countryCode="gb" svg style={{ width: "20px", height: "20px" }} title={i18n.language} />
      <div onBlur={() => markFieldTouched('content_en')}>
        <Editor
          editorState={editorState["en"]}
          editorClassName={`md-textarea form-control ${contentEnError ? "is-invalid" : ""}`}
          onEditorStateChange={e => onEditorStateChange(e, "en")}
          toolbar={EDITOR_TOOLBAR}
        />
      </div>
      {contentEnError && <div className="text-danger small mb-2">{contentEnError}</div>}
    </>
  );
}
